import React from 'react';
import { Modal, Box, Text, Table, Group, ThemeIcon, Stack } from '@mantine/core';
import { IconCalendarStats } from '@tabler/icons-react';
import type { IVariacionMensualMes } from '../../../../modelos/CotizadorNBS/entidades/IVariacionMensualMes';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export interface IEquipoVariacionMensual {
    Nombre: string;
    /** Flete unitario calculado (USD). */
    FleteUnitario: number;
    /** Precio de venta sin impuestos del equipo, unitario, en USD (en Colombia: el precio en COP / TRM). */
    PrecioVentaUsd: number;
}

interface ModalVariacionMensualVistaProps {
    equipo: IEquipoVariacionMensual | null;
    alCerrar: () => void;
    variacion: IVariacionMensualMes[];
    /** Para abrirlo encima de otro modal. */
    zIndex?: number;
}

/** USD con centavos; moneda local (COP) sin decimales, como en el resto de la cotización. */
const dinero = (valor: number, moneda: string) =>
    `$ ${valor.toLocaleString('es-CO', { maximumFractionDigits: moneda === 'USD' ? 2 : 0 })} ${moneda}`;

/**
 * Informativo para el comercial: cómo quedaría el flete unitario del equipo en cada mes con la variación
 * mensual del país (el flete sube por temporada), y el precio de venta con ese flete:
 * flete del mes = flete × (1 + %); precio del mes (USD) = precio USD + (flete del mes − flete).
 * No recalcula la cotización.
 */
export const ModalVariacionMensualVista: React.FC<ModalVariacionMensualVistaProps> = ({ equipo, alCerrar, variacion, zIndex }) => {
    const porcentajePorMes = new Map(variacion.map(v => [v.MesNumero, v.Porcentaje]));

    return (
        <Modal
            opened={equipo !== null}
            onClose={alCerrar}
            styles={{
                header: { backgroundColor: '#0074d914' },
                body: { padding: 'var(--mantine-spacing-md)', paddingTop: 'var(--mantine-spacing-xs)' }
            }}
            title={
                <Group gap="xs">
                    <ThemeIcon color="primary" size="md" variant="light" radius="md">
                        <IconCalendarStats size={18} />
                    </ThemeIcon>
                    <Text fw={600} fz="md">Variación mensual del flete</Text>
                </Group>
            }
            size="70rem"
            radius="md"
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            centered
            zIndex={zIndex}
        >
            {equipo && (
                <Stack gap="sm" mt="xs">
                    <Text size="sm" fw={600}>{equipo.Nombre}</Text>
                    {variacion.length === 0 && (
                        <Text size="sm" c="dimmed">El país no tiene variación mensual configurada: el flete es el mismo todos los meses.</Text>
                    )}
                    <Box style={{ border: '1px solid #dee2e6', borderRadius: 'var(--mantine-radius-sm)', overflow: 'hidden' }}>
                        <Table verticalSpacing={6} horizontalSpacing="md" withColumnBorders>
                            <Table.Thead style={{ background: '#f8f9fa' }}>
                                <Table.Tr>
                                    <Table.Th rowSpan={2} style={{ fontSize: '11px' }}>Mes</Table.Th>
                                    <Table.Th rowSpan={2} style={{ fontSize: '11px', textAlign: 'right' }}>Variación</Table.Th>
                                    <Table.Th colSpan={2} style={{ fontSize: '11px', textAlign: 'center' }}>Flete Unitario (USD)</Table.Th>
                                    <Table.Th colSpan={2} style={{ fontSize: '11px', textAlign: 'center' }}>Precio Venta (USD)</Table.Th>
                                </Table.Tr>
                                <Table.Tr>
                                    <Table.Th style={{ fontSize: '11px', textAlign: 'right' }}>Real</Table.Th>
                                    <Table.Th style={{ fontSize: '11px', textAlign: 'right' }}>Con variación</Table.Th>
                                    <Table.Th style={{ fontSize: '11px', textAlign: 'right' }}>Real</Table.Th>
                                    <Table.Th style={{ fontSize: '11px', textAlign: 'right' }}>Con variación</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {MESES.map((nombre, indice) => {
                                    const mes = indice + 1;
                                    const porcentaje = porcentajePorMes.get(mes) ?? 0;
                                    const flete = equipo.FleteUnitario * (1 + porcentaje / 100);
                                    // Todo en USD: el precio sube lo mismo que sube el flete.
                                    const precio = equipo.PrecioVentaUsd + (flete - equipo.FleteUnitario);
                                    return (
                                        <Table.Tr key={mes}>
                                            <Table.Td><Text fz="xs">{nombre}</Text></Table.Td>
                                            <Table.Td style={{ textAlign: 'right' }}>
                                                <Text fz="xs" fw={porcentaje !== 0 ? 600 : undefined} c={porcentaje > 0 ? 'orange.7' : porcentaje < 0 ? 'green.7' : 'dimmed'}>
                                                    {porcentaje > 0 ? '+' : ''}{porcentaje.toLocaleString('es-CO')} %
                                                </Text>
                                            </Table.Td>
                                            <Table.Td style={{ textAlign: 'right' }}><Text fz="xs" c="dimmed">{dinero(equipo.FleteUnitario, 'USD')}</Text></Table.Td>
                                            <Table.Td style={{ textAlign: 'right' }}><Text fz="xs" fw={porcentaje !== 0 ? 600 : undefined}>{dinero(flete, 'USD')}</Text></Table.Td>
                                            <Table.Td style={{ textAlign: 'right' }}><Text fz="xs" c="dimmed">{dinero(equipo.PrecioVentaUsd, 'USD')}</Text></Table.Td>
                                            <Table.Td style={{ textAlign: 'right' }}><Text fz="xs" fw={porcentaje !== 0 ? 600 : undefined}>{dinero(precio, 'USD')}</Text></Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>
                    </Box>
                    <Text size="xs" c="dimmed">
                        Estos valores son solo informativos: no afectan ni cambian los valores de la cotización.
                    </Text>
                </Stack>
            )}
        </Modal>
    );
};
